import React from 'react'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import Head from 'next/head'
import { getFooterData } from '~shared/api/footer/getFooterData'
import { getPopularPrograms } from '~shared/api/programs-popular/getPopularPrograms'
import { getCategoriesData } from '~shared/api/categories/getCategories'
import { HeaderUpdated } from '~widgets/header/components/header-updated'
import { Footer } from '~widgets/footer/components/footer'
import { Breadcrumbs } from '~widgets/breadcrumbs/breadcrumbs'
import { getJournalData } from '~shared/api/single-journal-data/single-journal-data'
import { PATHS } from '~shared/constants/paths'
import { RelatedCourses } from '~pages/journal-page/journal-related-course'
import { JournalCardMainSection } from '~pages/journal-page/journal-card-main-section'
import { JournalCardBody } from '~pages/journal-page/journal-card-body'
import { DownloadFreeMaterials } from '~pages/journal-page/journal-pdf-download'
import { Social } from '~pages/journal-page/socials'
import { JournalSimilarArticle } from '~pages/journal-page/journal-similar-article'
import { RelatedCoursesBanner } from '~pages/journal-page/journal-related-course-banner'
import { getJournalPath } from '~shared/utils/getJournalPath'

export type JournalPageProps = {
  footer: Awaited<ReturnType<typeof getFooterData>>
  popularPrograms: Awaited<Promise<ReturnType<typeof getPopularPrograms>>>
  categories: Awaited<ReturnType<typeof getCategoriesData>>
  journal: Awaited<ReturnType<typeof getJournalData>>
}

export const JournalPage = ({
  categories,
  popularPrograms,
  footer,
  journal
}: JournalPageProps) => {
  const { asPath, locale } = useRouter()
  const seoParams = {
    title:
      journal?.seo?.metaTitle ??
      'Информационная статья | Журнал MITM.Institute',
    desc:
      journal?.seo?.metaDescription ??
      'Статья блога онлайн-института МИТМ. Читайте самые свежие публикации по актуальным темам: психология, дизайн, маркетинг, строительство, менеджмент, экономика, образование и многое другое.',
    canonical:
      journal?.seo?.canonicalURL ||
      `${PATHS.front.root.index}${journal ? getJournalPath(journal.tag?.slug, journal.slug) : asPath}`
  }

  return (
    <>
      <NextSeo
        title={seoParams.title}
        description={seoParams.desc}
        canonical={seoParams.canonical}
        openGraph={{
          url: seoParams.canonical,
          title: seoParams.title,
          description: seoParams.desc,
          images: [
            {
              url: `${PATHS.front.root.index}${PATHS.front.public.assets.imgs.icons.mitmLogo512.index}`,
              width: 512,
              height: 512,
              alt: 'Московский Институт Технологий и Управления',
              type: 'image/png'
            }
          ],
          site_name: 'Московский Институт Технологий и Управления'
        }}
      />
      {locale !== 'ru' && (
        <Head>
          <meta name='robots' content='noindex, nofollow' />
        </Head>
      )}

      <HeaderUpdated
        categories={categories}
        popularPrograms={popularPrograms}
      />
      <main className='pb-[100px]'>
        <Breadcrumbs journal={journal} />
        <div className='grid lg:grid-cols-[2fr_1fr] gap-[32px] mx-auto px-[16px] box-border max-w-[672px] md:max-w-[864px] 2lg:max-w-[1280px]'>
          <article className='w-full min-w-0' style={{ overflowX: 'hidden' }}>
            {journal && <JournalCardMainSection journal={journal} />}
            {journal && <JournalCardBody journal={journal} />}
            {journal?.programs && journal?.programs?.length > 0 && (
              <RelatedCourses programs={journal?.programs} />
            )}
            <Social
              journalSlug={journal?.slug}
              tagSlug={journal?.tag?.slug ?? ''}
              className='my-[48px]'
            />
            {journal?.linkedJournal &&
              journal?.linkedJournal.find(journal => journal?.journal) && (
                <JournalSimilarArticle journal={journal} />
              )}
          </article>
          <aside
            className={'md:sticky md:top-[24px] md:self-start w-full z-10'}>
            {journal?.programs && journal?.programs?.length > 0 && (
              <RelatedCoursesBanner programs={journal?.programs} asBanner />
            )}
            {journal?.pdf && <DownloadFreeMaterials pdf={journal?.pdf} />}
          </aside>
        </div>
      </main>
      <Footer data={footer} />
    </>
  )
}
